import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE from '../config';
import toast from "react-hot-toast";
import { creditAwareFetch } from '../hooks/creditAwareFetch';
import SequenceConfigPanel from './SequenceConfigPanel';

const SIGNAL_LABELS = { funding: 'Funding Round', hiring: 'Hiring Activity', leadership_change: 'Leadership Change', news: 'Company News', tech_change: 'Technology Change' };
const SIGNAL_COLOURS = { funding: '#276749', hiring: '#2B6CB0', leadership_change: '#8B1A1A', news: '#B7791F', tech_change: '#553C9A' };
const SIGNAL_BG = { funding: '#F0FFF4', hiring: '#EBF8FF', leadership_change: '#FFF5F5', news: '#FFFFF0', tech_change: '#FAF5FF' };
const CONTACT_PAGE_SIZE = 20;
const SIGNAL_PAGE_SIZE = 20;

function SweepBadge({ status }) {
  const map = {
    fresh: { label: '✓ Up to date', color: '#276749', bg: '#F0FFF4', border: '#9AE6B4' },
    due: { label: '⚠ Needs scan', color: '#B7791F', bg: '#FFFBEB', border: '#F6E05E' },
    never: { label: '○ Not scanned', color: '#888', bg: '#F7F7F7', border: '#E2E8F0' },
  };
  const s = map[status] || map.never;
  return (
    <span style={{ fontSize: '0.72rem', fontWeight: '600', color: s.color, backgroundColor: s.bg, border: '1px solid ' + s.border, borderRadius: '20px', padding: '0.2rem 0.6rem' }}>
      {s.label}
    </span>
  );
}

export default function CompanyPanel({ company, clientId, onClose, onContactOpen, onScan, scanningId, sweepMeta }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('intel');
  const [signals, setSignals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [contactPage, setContactPage] = useState(0);
  const [signalPage, setSignalPage] = useState(0);
  const [contactSearch, setContactSearch] = useState('');
  const [findingContacts, setFindingContacts] = useState(false);
  const [contactResults, setContactResults] = useState(null);
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [importingContacts, setImportingContacts] = useState(false);
  const [contactImportResult, setContactImportResult] = useState(null);
  const [contactSearchError, setContactSearchError] = useState(null);
  const [verifiedIntel, setVerifiedIntel] = useState(null);
  const [applyingIntel, setApplyingIntel] = useState(false);

  const meta = sweepMeta?.[company.id] || {};
  const sweepStatus = meta.sweep_status || 'never';

  useEffect(() => {
    if (!company) return;
    setLoading(true);
    setActiveTab('intel');
    setContactPage(0);
    setSignalPage(0);
    setContactSearch('');
    Promise.all([
      fetch(`${API_BASE}/companies/${company.id}/signals`).then(r => r.json()),
      fetch(`${API_BASE}/companies/${company.id}/contacts`).then(r => r.json()),
      fetch(`${API_BASE}/companies/${company.id}/notes`).then(r => r.json()).catch(() => []),
      fetch(`${API_BASE}/companies/${company.id}/verified-intel`).then(r => r.json()).catch(() => ({ verified: null })),
    ]).then(([signalData, contactData, noteData, intelData]) => {
      setSignals(signalData.signals || []);
      setContacts(Array.isArray(contactData) ? contactData : []);
      setNotes(Array.isArray(noteData) ? noteData : []);
      setVerifiedIntel(intelData.verified || null);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [company?.id]);

  function startEdit() {
    setEditForm({
      name: company.name || '',
      sector: company.sector || '',
      size: company.size || '',
      location: company.location || '',
      website: company.website || '',
      linkedin_url: company.linkedin_url || '',
    });
    setEditing(true);
  }

  async function handleSaveEdit() {
    setSavingEdit(true);
    try {
      await fetch(`${API_BASE}/companies/${company.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      Object.assign(company, editForm);
      setEditing(false);
    } catch (e) {}
    setSavingEdit(false);
  }

  async function handleApplyIntel() {
    if (!verifiedIntel) return;
    setApplyingIntel(true);
    try {
      await fetch(`${API_BASE}/companies/${company.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(verifiedIntel)
      });
      Object.assign(company, verifiedIntel);
      setVerifiedIntel(null);
      toast.success('Intel applied to ' + company.name);
    } catch (e) {
      toast.error('Could not apply intel: ' + e.message);
    }
    setApplyingIntel(false);
  }

  async function handleAddNote() {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const res = await fetch(`${API_BASE}/companies/${company.id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: clientId, content: newNote.trim() })
      });
      const note = await res.json();
      setNotes(prev => [...prev, note]);
      setNewNote('');
    } catch (e) {}
    setSavingNote(false);
  }

  async function handleFindContacts() {
    setFindingContacts(true);
    setContactSearchError(null);
    setContactResults(null);
    setContactImportResult(null);
    setSelectedContactIds(new Set());
    const toastId = toast.loading('Searching for contacts at ' + company.name + '...');
    try {
      const res = await creditAwareFetch(API_BASE + '/companies/' + company.id + '/find-contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!res) { toast.dismiss(toastId); setFindingContacts(false); return; }
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setContactResults(data);
      const count = (data.contacts && data.contacts.length) || 0;
      if (count > 0) {
        toast.success('Found ' + count + ' contact' + (count === 1 ? '' : 's'), { id: toastId });
      } else {
        toast.success('No matching contacts found', { id: toastId });
      }
    } catch (e) {
      setContactSearchError(e.message);
      toast.error('Search failed: ' + e.message, { id: toastId });
    }
    setFindingContacts(false);
  }

  async function handleImportContacts() {
    if (!selectedContactIds.size || !contactResults) return;
    setImportingContacts(true);
    const importCount = selectedContactIds.size;
    const toastId = toast.loading('Importing ' + importCount + ' contact' + (importCount === 1 ? '' : 's') + '...');
    try {
      const toImport = contactResults.contacts.filter((_, i) => selectedContactIds.has(i));
      const res = await creditAwareFetch(API_BASE + '/companies/' + company.id + '/import-contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contacts: toImport })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setContactImportResult(data);
      toast.success('Imported ' + importCount + ' contact' + (importCount === 1 ? '' : 's'), { id: toastId });
      setSelectedContactIds(new Set());
      // Refresh contacts list
      if (!res) return; // 402 limit_reached — modal handled by creditAwareFetch
      const res2 = await fetch(API_BASE + '/companies/' + company.id + '/contacts').then(r => r.json());
      setContacts(Array.isArray(res2) ? res2 : []);
      // Mark imported in results
      setContactResults(prev => ({
        ...prev,
        contacts: prev.contacts.map((c, i) => selectedContactIds.has(i) ? { ...c, already_exists: true } : c)
      }));
    } catch (e) {
      setContactSearchError(e.message);
      toast.error('Import failed: ' + e.message);
    }
    setImportingContacts(false);
  }

  // Filtered + paged contacts
  const filteredContacts = contactSearch.trim()
    ? contacts.filter(c =>
        (c.full_name || '').toLowerCase().includes(contactSearch.toLowerCase()) ||
        (c.job_title || '').toLowerCase().includes(contactSearch.toLowerCase()) ||
        (c.email || '').toLowerCase().includes(contactSearch.toLowerCase())
      )
    : contacts;
  const pagedContacts = filteredContacts.slice(contactPage * CONTACT_PAGE_SIZE, (contactPage + 1) * CONTACT_PAGE_SIZE);
  const totalContactPages = Math.ceil(filteredContacts.length / CONTACT_PAGE_SIZE);

  // Paged signals
  const pagedSignals = signals.slice(signalPage * SIGNAL_PAGE_SIZE, (signalPage + 1) * SIGNAL_PAGE_SIZE);
  const totalSignalPages = Math.ceil(signals.length / SIGNAL_PAGE_SIZE);

  const tabs = [
    { key: 'intel', label: 'Intel' },
    { key: 'signals', label: `Signals${signals.length > 0 ? ' (' + signals.length + ')' : ''}` },
    { key: 'contacts', label: `Contacts${contacts.length > 0 ? ' (' + contacts.length + ')' : ''}` },
    { key: 'notes', label: `Notes${notes.length > 0 ? ' (' + notes.length + ')' : ''}` },
    { key: 'seq-settings', label: 'Seq Settings' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div style={{ width: '620px', height: '100vh', backgroundColor: '#fff', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '0.5px solid #E8E4E0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
                {company.website && (
                  <img src={'https://www.google.com/s2/favicons?domain=' + company.website.replace(/^https?:\/\//, '') + '&sz=32'} alt="" style={{ width: '20px', height: '20px', borderRadius: '4px' }} onError={e => e.target.style.display='none'} />
                )}
                <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: '600', color: '#1A1A1A' }}>{company.name}</h2>
                <SweepBadge status={sweepStatus} />
              </div>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#888' }}>
                {[company.sector, company.size, company.location].filter(Boolean).join(' · ')}
              </p>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: '#888', cursor: 'pointer', flexShrink: 0 }}>×</button>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            <button onClick={() => onScan(company)} disabled={scanningId === company.id}
              style={{ fontSize: '0.78rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.35rem 0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', opacity: scanningId === company.id ? 0.7 : 1 }}>
              {scanningId === company.id ? (
                <><span style={{ display: 'inline-block', width: '8px', height: '8px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />Scanning...</>
              ) : 'Scan for signals'}
            </button>
            <button onClick={() => navigate('/clients/' + clientId + '/find-similar?seed=' + company.id)}
              style={{ fontSize: '0.78rem', color: '#8B1A1A', background: '#fff', border: '1px solid #8B1A1A', borderRadius: '6px', padding: '0.35rem 0.85rem', cursor: 'pointer' }}>
              Find similar →
            </button>
            {company.website && (
              <a href={company.website.startsWith('http') ? company.website : 'https://' + company.website} target="_blank" rel="noreferrer"
                style={{ fontSize: '0.78rem', color: '#2B6CB0', background: 'none', border: '1px solid #BFD7F5', borderRadius: '6px', padding: '0.35rem 0.75rem', textDecoration: 'none' }}>
                Website →
              </a>
            )}
            {company.linkedin_url && (
              <a href={company.linkedin_url.startsWith('http') ? company.linkedin_url : 'https://' + company.linkedin_url} target="_blank" rel="noreferrer"
                style={{ fontSize: '0.78rem', color: '#1A56A0', background: 'none', border: '1px solid #BFD7F5', borderRadius: '6px', padding: '0.35rem 0.75rem', textDecoration: 'none' }}>
                LinkedIn →
              </a>
            )}
            <button onClick={startEdit}
              style={{ fontSize: '0.78rem', color: '#4A5568', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.35rem 0.75rem', cursor: 'pointer' }}>
              Edit
            </button>
          </div>

          {/* Sweep meta */}
          {meta.last_swept_at && (
            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.72rem', color: '#888', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
              <span>Last scanned: <strong style={{ color: '#1A1A1A' }}>{new Date(meta.last_swept_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</strong></span>
              {meta.next_sweep_due && <span>Next due: <strong style={{ color: '#1A1A1A' }}>{new Date(meta.next_sweep_due).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</strong></span>}
              <span>Total scans: <strong style={{ color: '#1A1A1A' }}>{meta.sweep_count || 0}</strong></span>
            </div>
          )}

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 0, marginTop: '0.5rem', borderBottom: '0.5px solid #E8E4E0' }}>
            {tabs.map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                style={{ padding: '0.4rem 0.85rem', background: 'none', border: 'none', borderBottom: activeTab === tab.key ? '2px solid #8B1A1A' : '2px solid transparent', color: activeTab === tab.key ? '#8B1A1A' : '#888', fontSize: '0.8rem', cursor: 'pointer', fontWeight: activeTab === tab.key ? '500' : '400' }}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1.5rem' }}>
          {loading ? (
            <p style={{ color: '#888', fontSize: '0.85rem' }}>Loading...</p>
          ) : (
            <>
              {/* INTEL TAB */}
              {activeTab === 'intel' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  {verifiedIntel && (() => {
                    const fields = Object.entries(verifiedIntel).filter(([k]) => !['verified_count','last_verified_at'].includes(k));
                    const fieldLabels = { sector: 'Sector', size: 'Size', location: 'Location', linkedin_url: 'LinkedIn URL' };
                    return (
                      <div style={{ backgroundColor: '#EBF8FF', border: '1px solid #BEE3F8', borderLeft: '4px solid #2B6CB0', borderRadius: '8px', padding: '1rem', marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                          <div>
                            <p style={{ margin: '0 0 0.2rem', fontSize: '0.78rem', fontWeight: '600', color: '#2B6CB0' }}>Platform intelligence available</p>
                            <p style={{ margin: 0, fontSize: '0.72rem', color: '#4A5568' }}>Verified by {verifiedIntel.verified_count} update{verifiedIntel.verified_count !== 1 ? 's' : ''} across the platform · {new Date(verifiedIntel.last_verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                          </div>
                          <button onClick={() => setVerifiedIntel(null)} style={{ background: 'none', border: 'none', color: '#888', fontSize: '1rem', cursor: 'pointer', flexShrink: 0 }}>×</button>
                        </div>
                        {/* Comparison table */}
                        <div style={{ backgroundColor: '#fff', borderRadius: '6px', border: '0.5px solid #BEE3F8', overflow: 'hidden', marginBottom: '0.75rem' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', backgroundColor: '#EBF8FF', padding: '0.4rem 0.75rem', borderBottom: '0.5px solid #BEE3F8' }}>
                            <span style={{ fontSize: '0.68rem', fontWeight: '600', color: '#2B6CB0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Field</span>
                            <span style={{ fontSize: '0.68rem', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current</span>
                            <span style={{ fontSize: '0.68rem', fontWeight: '600', color: '#2B6CB0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Platform verified</span>
                          </div>
                          {fields.map(([key, value], i) => (
                            <div key={key} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', padding: '0.5rem 0.75rem', borderBottom: i < fields.length - 1 ? '0.5px solid #EBF8FF' : 'none', alignItems: 'center' }}>
                              <span style={{ fontSize: '0.75rem', color: '#4A5568', fontWeight: '500' }}>{fieldLabels[key] || key}</span>
                              <span style={{ fontSize: '0.75rem', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{company[key] || '—'}</span>
                              <span style={{ fontSize: '0.75rem', color: '#276749', fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
                            </div>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button onClick={handleApplyIntel} disabled={applyingIntel}
                            style={{ fontSize: '0.78rem', color: '#fff', background: '#2B6CB0', border: 'none', borderRadius: '6px', padding: '0.35rem 0.85rem', cursor: 'pointer', opacity: applyingIntel ? 0.6 : 1, fontWeight: '500' }}>
                            {applyingIntel ? 'Applying...' : 'Apply all changes'}
                          </button>
                          <button onClick={() => setVerifiedIntel(null)}
                            style={{ fontSize: '0.78rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.35rem 0.75rem', cursor: 'pointer' }}>
                            Dismiss
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {editing ? (
                    <div style={{ backgroundColor: '#FAFAF8', border: '0.5px solid #8B1A1A', borderRadius: '8px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                      {[
                        { label: 'Company name', key: 'name' },
                        { label: 'Sector', key: 'sector' },
                        { label: 'Size', key: 'size' },
                        { label: 'Location', key: 'location' },
                        { label: 'Website', key: 'website' },
                        { label: 'LinkedIn URL', key: 'linkedin_url' },
                      ].map(f => (
                        <div key={f.key}>
                          <label style={{ display: 'block', fontSize: '0.72rem', color: '#888', marginBottom: '0.2rem', fontWeight: '500' }}>{f.label}</label>
                          <input value={editForm[f.key]} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                            style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.6rem', fontSize: '0.82rem', color: '#1A1A1A', backgroundColor: '#fff', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }} />
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                        <button onClick={handleSaveEdit} disabled={savingEdit}
                          style={{ fontSize: '0.78rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer', opacity: savingEdit ? 0.6 : 1 }}>
                          {savingEdit ? 'Saving...' : 'Save changes'}
                        </button>
                        <button onClick={() => setEditing(false)}
                          style={{ fontSize: '0.78rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.4rem 0.75rem', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p style={{ margin: '0 0 0.6rem', fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '500' }}>Company information</p>
                      <div style={{ backgroundColor: '#FAFAF8', border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '0.85rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                        {company.sector && <p style={{ margin: 0, fontSize: '0.82rem', color: '#1A1A1A' }}><span style={{ color: '#888', marginRight: '0.5rem', minWidth: '80px', display: 'inline-block' }}>Sector</span>{company.sector}</p>}
                        {company.size && <p style={{ margin: 0, fontSize: '0.82rem', color: '#1A1A1A' }}><span style={{ color: '#888', marginRight: '0.5rem', minWidth: '80px', display: 'inline-block' }}>Size</span>{company.size}</p>}
                        {company.location && <p style={{ margin: 0, fontSize: '0.82rem', color: '#1A1A1A' }}><span style={{ color: '#888', marginRight: '0.5rem', minWidth: '80px', display: 'inline-block' }}>Location</span>{company.location}</p>}
                        {company.website && <p style={{ margin: 0, fontSize: '0.82rem' }}><span style={{ color: '#888', marginRight: '0.5rem', minWidth: '80px', display: 'inline-block' }}>Website</span><a href={company.website.startsWith('http') ? company.website : 'https://' + company.website} target="_blank" rel="noreferrer" style={{ color: '#2B6CB0' }}>{company.website}</a></p>}
                        {company.linkedin_url && <p style={{ margin: 0, fontSize: '0.82rem' }}><span style={{ color: '#888', marginRight: '0.5rem', minWidth: '80px', display: 'inline-block' }}>LinkedIn</span><a href={company.linkedin_url.startsWith('http') ? company.linkedin_url : 'https://' + company.linkedin_url} target="_blank" rel="noreferrer" style={{ color: '#2B6CB0' }}>View profile</a></p>}
                        {company.keywords && <p style={{ margin: 0, fontSize: '0.82rem', color: '#1A1A1A' }}><span style={{ color: '#888', marginRight: '0.5rem', minWidth: '80px', display: 'inline-block' }}>Keywords</span>{company.keywords}</p>}
                        {company.revenue && <p style={{ margin: 0, fontSize: '0.82rem', color: '#1A1A1A' }}><span style={{ color: '#888', marginRight: '0.5rem', minWidth: '80px', display: 'inline-block' }}>Revenue</span>{company.revenue}</p>}
                      </div>
                    </div>
                  )}

                  {/* Latest signals preview */}
                  {signals.length > 0 && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                        <p style={{ margin: 0, fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '500' }}>Latest signals</p>
                        <button onClick={() => setActiveTab('signals')} style={{ fontSize: '0.72rem', color: '#8B1A1A', background: 'none', border: 'none', cursor: 'pointer' }}>View all {signals.length} →</button>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {signals.slice(0, 3).map(s => (
                          <div key={s.id} style={{ backgroundColor: SIGNAL_BG[s.signal_type] || '#F7F7F7', borderRadius: '8px', padding: '0.75rem 1rem', borderLeft: '3px solid ' + (SIGNAL_COLOURS[s.signal_type] || '#888') }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                              <span style={{ fontSize: '0.68rem', fontWeight: '600', color: SIGNAL_COLOURS[s.signal_type] || '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{SIGNAL_LABELS[s.signal_type] || s.signal_type}</span>
                              <span style={{ fontSize: '0.68rem', color: '#888' }}>{new Date(s.detected_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                            </div>
                            <p style={{ margin: 0, fontSize: '0.82rem', color: '#1A1A1A', lineHeight: '1.4' }}>{s.signal_data?.summary}</p>
                            {s.source_url && <a href={s.source_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.72rem', color: '#2B6CB0', textDecoration: 'none' }}>{s.source} →</a>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {signals.length === 0 && (
                    <div style={{ backgroundColor: '#FAFAF8', border: '0.5px solid #E8E4E0', borderRadius: '8px', padding: '1.5rem', textAlign: 'center' }}>
                      <p style={{ margin: '0 0 0.5rem', fontSize: '0.85rem', color: '#888' }}>No signals detected yet.</p>
                      <button onClick={() => onScan(company)} style={{ fontSize: '0.8rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer' }}>Scan now</button>
                    </div>
                  )}

                  {/* Contacts preview */}
                  {contacts.length > 0 && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
                        <p style={{ margin: 0, fontSize: '0.72rem', color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: '500' }}>Contacts ({contacts.length})</p>
                        <button onClick={() => setActiveTab('contacts')} style={{ fontSize: '0.72rem', color: '#8B1A1A', background: 'none', border: 'none', cursor: 'pointer' }}>View all →</button>
                      </div>
                      <div style={{ backgroundColor: '#FAFAF8', border: '0.5px solid #E8E4E0', borderRadius: '8px', overflow: 'hidden' }}>
                        {contacts.slice(0, 4).map((ct, i) => (
                          <div key={ct.id} onClick={() => onContactOpen(ct.id)}
                            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.65rem 1rem', borderBottom: i < Math.min(contacts.length, 4) - 1 ? '0.5px solid #F0EDE9' : 'none', cursor: 'pointer' }}
                            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#F5F2EF'}
                            onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                            <div>
                              <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: '500', color: '#1A1A1A' }}>{ct.full_name}</p>
                              <p style={{ margin: 0, fontSize: '0.72rem', color: '#888' }}>{ct.job_title || ''}</p>
                            </div>
                            <span style={{ fontSize: '0.72rem', color: '#8B1A1A' }}>View →</span>
                          </div>
                        ))}
                        {contacts.length > 4 && (
                          <div style={{ padding: '0.5rem 1rem', borderTop: '0.5px solid #E8E4E0', textAlign: 'center' }}>
                            <button onClick={() => setActiveTab('contacts')} style={{ fontSize: '0.75rem', color: '#8B1A1A', background: 'none', border: 'none', cursor: 'pointer' }}>
                              + {contacts.length - 4} more contacts
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* SIGNALS TAB */}
              {activeTab === 'signals' && (
                <div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem', marginBottom: '1rem' }}>
                    {signals.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                        <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem' }}>No signals detected yet.</p>
                        <button onClick={() => onScan(company)} style={{ fontSize: '0.8rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '6px', padding: '0.4rem 0.85rem', cursor: 'pointer' }}>Scan now</button>
                      </div>
                    ) : pagedSignals.map(s => (
                      <div key={s.id} style={{ backgroundColor: SIGNAL_BG[s.signal_type] || '#F7F7F7', borderRadius: '8px', padding: '1rem', borderLeft: '3px solid ' + (SIGNAL_COLOURS[s.signal_type] || '#888') }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.35rem' }}>
                          <span style={{ fontSize: '0.68rem', fontWeight: '600', color: SIGNAL_COLOURS[s.signal_type] || '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{SIGNAL_LABELS[s.signal_type] || s.signal_type}</span>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            <span style={{ fontSize: '0.68rem', fontWeight: '600', color: SIGNAL_COLOURS[s.signal_type] || '#888' }}>Score {s.relevance_score}/10</span>
                            <span style={{ fontSize: '0.68rem', color: '#888' }}>{new Date(s.detected_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          </div>
                        </div>
                        <p style={{ margin: '0 0 0.3rem', fontSize: '0.85rem', color: '#1A1A1A', lineHeight: '1.4', fontWeight: '500' }}>{s.signal_data?.summary}</p>
                        {s.source_url ? (
                          <a href={s.source_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.72rem', color: '#2B6CB0', textDecoration: 'none' }}>{s.source} →</a>
                        ) : s.source ? (
                          <span style={{ fontSize: '0.72rem', color: '#888' }}>{s.source}</span>
                        ) : null}
                      </div>
                    ))}
                  </div>
                  {totalSignalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.5rem', borderTop: '0.5px solid #E8E4E0' }}>
                      <span style={{ fontSize: '0.75rem', color: '#888' }}>{signalPage * SIGNAL_PAGE_SIZE + 1}–{Math.min((signalPage + 1) * SIGNAL_PAGE_SIZE, signals.length)} of {signals.length}</span>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button onClick={() => setSignalPage(p => Math.max(0, p - 1))} disabled={signalPage === 0} style={{ background: 'none', border: '1px solid #D4CFC9', borderRadius: '4px', color: signalPage === 0 ? '#CCC' : '#4A5568', padding: '0.2rem 0.6rem', fontSize: '0.75rem', cursor: signalPage === 0 ? 'default' : 'pointer' }}>Prev</button>
                        <button onClick={() => setSignalPage(p => p + 1)} disabled={(signalPage + 1) * SIGNAL_PAGE_SIZE >= signals.length} style={{ background: 'none', border: '1px solid #D4CFC9', borderRadius: '4px', color: (signalPage + 1) * SIGNAL_PAGE_SIZE >= signals.length ? '#CCC' : '#4A5568', padding: '0.2rem 0.6rem', fontSize: '0.75rem', cursor: (signalPage + 1) * SIGNAL_PAGE_SIZE >= signals.length ? 'default' : 'pointer' }}>Next</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* CONTACTS TAB */}
              {activeTab === 'contacts' && (
                <div>
                  {/* Find contacts toolbar */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={contactSearch}
                      onChange={e => { setContactSearch(e.target.value); setContactPage(0); }}
                      placeholder={'Search ' + contacts.length + ' contacts...'}
                      style={{ flex: 1, border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.82rem', color: '#1A1A1A', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }}
                    />
                    <button onClick={handleFindContacts} disabled={findingContacts}
                      style={{ fontSize: '0.78rem', color: '#fff', background: '#6B46C1', border: 'none', borderRadius: '6px', padding: '0.5rem 0.85rem', cursor: 'pointer', whiteSpace: 'nowrap', opacity: findingContacts ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: '0.35rem', flexShrink: 0 }}>
                      {findingContacts ? (
                        <><span style={{ display: 'inline-block', width: '8px', height: '8px', border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />Finding...</>
                      ) : 'Find contacts'}
                    </button>
                  </div>

                  {contactSearchError && <p style={{ fontSize: '0.75rem', color: '#C53030', margin: '0 0 0.75rem' }}>{contactSearchError}</p>}

                  {/* People search results */}
                  {contactResults && (
                    <div style={{ backgroundColor: '#FAF5FF', border: '0.5px solid #D4B8F5', borderRadius: '8px', padding: '0.85rem', marginBottom: '0.75rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <p style={{ margin: 0, fontSize: '0.72rem', fontWeight: '600', color: '#6B46C1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {contactResults.contacts.length} people found
                        </p>
                        <div style={{ display: 'flex', gap: '0.4rem' }}>
                          <button onClick={() => setSelectedContactIds(new Set(contactResults.contacts.map((c, i) => c.already_exists ? null : i).filter(i => i !== null)))}
                            style={{ fontSize: '0.7rem', color: '#6B46C1', background: 'none', border: '1px solid #D4B8F5', borderRadius: '20px', padding: '0.15rem 0.5rem', cursor: 'pointer' }}>
                            Select all new
                          </button>
                          <button onClick={() => setSelectedContactIds(new Set())}
                            style={{ fontSize: '0.7rem', color: '#888', background: 'none', border: '1px solid #D4CFC9', borderRadius: '20px', padding: '0.15rem 0.5rem', cursor: 'pointer' }}>
                            Clear
                          </button>
                          {selectedContactIds.size > 0 && (
                            <button onClick={handleImportContacts} disabled={importingContacts}
                              style={{ fontSize: '0.7rem', color: '#fff', background: '#6B46C1', border: 'none', borderRadius: '20px', padding: '0.15rem 0.6rem', cursor: 'pointer', fontWeight: '600', opacity: importingContacts ? 0.6 : 1 }}>
                              {importingContacts ? 'Adding...' : 'Add ' + selectedContactIds.size}
                            </button>
                          )}
                        </div>
                      </div>

                      {contactImportResult && (
                        <div style={{ padding: '0.4rem 0.6rem', backgroundColor: '#F0FFF4', border: '0.5px solid #9AE6B4', borderRadius: '6px', fontSize: '0.75rem', color: '#276749', marginBottom: '0.5rem' }}>
                          {contactImportResult.added} added{contactImportResult.skipped > 0 ? ', ' + contactImportResult.skipped + ' skipped' : ''}.
                        </div>
                      )}

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', maxHeight: '280px', overflowY: 'auto' }}>
                        {contactResults.contacts.map((contact, i) => (
                          <div key={i}
                            onClick={() => {
                              if (contact.already_exists) return;
                              const next = new Set(selectedContactIds);
                              if (next.has(i)) next.delete(i);
                              else next.add(i);
                              setSelectedContactIds(next);
                            }}
                            style={{
                              display: 'flex', gap: '0.5rem', padding: '0.55rem 0.65rem',
                              backgroundColor: contact.already_exists ? '#FAFAF8' : selectedContactIds.has(i) ? '#F5F0FF' : '#fff',
                              border: '0.5px solid ' + (selectedContactIds.has(i) ? '#D4B8F5' : '#E8E4E0'),
                              borderLeft: '3px solid ' + (contact.already_exists ? '#D4CFC9' : selectedContactIds.has(i) ? '#6B46C1' : '#E8E4E0'),
                              borderRadius: '6px', cursor: contact.already_exists ? 'default' : 'pointer',
                              opacity: contact.already_exists ? 0.5 : 1
                            }}>
                            <input type="checkbox" checked={selectedContactIds.has(i)} disabled={contact.already_exists} onChange={() => {}}
                              style={{ flexShrink: 0, marginTop: '2px', accentColor: '#6B46C1' }} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.1rem', flexWrap: 'wrap' }}>
                                <p style={{ margin: 0, fontSize: '0.82rem', fontWeight: '500', color: '#1A1A1A' }}>{contact.full_name}</p>
                                {contact.already_exists && <span style={{ fontSize: '0.65rem', color: '#276749', backgroundColor: '#F0FFF4', border: '1px solid #9AE6B4', borderRadius: '20px', padding: '0.1rem 0.35rem', fontWeight: '500' }}>Already exists</span>}
                              </div>
                              <p style={{ margin: 0, fontSize: '0.72rem', color: '#888' }}>
                                {contact.job_title}{contact.location ? ' · ' + contact.location : ''}
                              </p>
                              {contact.linkedin_url && (
                                <a href={contact.linkedin_url} target="_blank" rel="noreferrer"
                                  onClick={e => e.stopPropagation()}
                                  style={{ fontSize: '0.68rem', color: '#1A56A0', textDecoration: 'none' }}>
                                  LinkedIn →
                                </a>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
                    {filteredContacts.length === 0 ? (
                      <p style={{ color: '#888', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>No contacts found.</p>
                    ) : pagedContacts.map(ct => (
                      <div key={ct.id} onClick={() => onContactOpen(ct.id)}
                        style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #8B1A1A', borderRadius: '8px', padding: '0.85rem 1.25rem', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'}
                        onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                        <div>
                          <p style={{ margin: '0 0 0.15rem', fontSize: '0.88rem', fontWeight: '500', color: '#1A1A1A' }}>{ct.full_name}</p>
                          <p style={{ margin: 0, fontSize: '0.75rem', color: '#888' }}>{[ct.job_title, ct.email].filter(Boolean).join(' · ')}</p>
                        </div>
                        <span style={{ fontSize: '0.75rem', color: '#8B1A1A', flexShrink: 0 }}>View →</span>
                      </div>
                    ))}
                  </div>
                  {totalContactPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.5rem', borderTop: '0.5px solid #E8E4E0' }}>
                      <span style={{ fontSize: '0.75rem', color: '#888' }}>{contactPage * CONTACT_PAGE_SIZE + 1}–{Math.min((contactPage + 1) * CONTACT_PAGE_SIZE, filteredContacts.length)} of {filteredContacts.length}</span>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button onClick={() => setContactPage(p => Math.max(0, p - 1))} disabled={contactPage === 0} style={{ background: 'none', border: '1px solid #D4CFC9', borderRadius: '4px', color: contactPage === 0 ? '#CCC' : '#4A5568', padding: '0.2rem 0.6rem', fontSize: '0.75rem', cursor: contactPage === 0 ? 'default' : 'pointer' }}>Prev</button>
                        <button onClick={() => setContactPage(p => p + 1)} disabled={(contactPage + 1) * CONTACT_PAGE_SIZE >= filteredContacts.length} style={{ background: 'none', border: '1px solid #D4CFC9', borderRadius: '4px', color: (contactPage + 1) * CONTACT_PAGE_SIZE >= filteredContacts.length ? '#CCC' : '#4A5568', padding: '0.2rem 0.6rem', fontSize: '0.75rem', cursor: (contactPage + 1) * CONTACT_PAGE_SIZE >= filteredContacts.length ? 'default' : 'pointer' }}>Next</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* NOTES TAB */}
              {activeTab === 'notes' && (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {/* Messages */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                    {notes.length === 0 ? (
                      <p style={{ color: '#888', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0', fontStyle: 'italic' }}>No notes yet. Add the first one below.</p>
                    ) : (() => {
                      const BUBBLE_COLOURS = [
                        { bg: '#FFF5F5', border: '#FEB2B2', text: '#742A2A' },
                        { bg: '#EBF8FF', border: '#BEE3F8', text: '#2A4365' },
                        { bg: '#F0FFF4', border: '#9AE6B4', text: '#22543D' },
                        { bg: '#FFFBEB', border: '#FBD38D', text: '#744210' },
                        { bg: '#FAF5FF', border: '#D6BCFA', text: '#44337A' },
                        { bg: '#FFF5F7', border: '#FBB6CE', text: '#702459' },
                      ];
                      const authorMap = {};
                      let authorIndex = 0;
                      return [...notes].reverse().map(note => {
                        const author = note.author_name || 'Team';
                        if (authorMap[author] === undefined) {
                          authorMap[author] = authorIndex % BUBBLE_COLOURS.length;
                          authorIndex++;
                        }
                        const colour = BUBBLE_COLOURS[authorMap[author]];
                        return (
                          <div key={note.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.2rem' }}>
                            <div style={{ backgroundColor: colour.bg, border: '1px solid ' + colour.border, borderRadius: '12px 12px 12px 2px', padding: '0.65rem 0.9rem', maxWidth: '85%' }}>
                              <p style={{ margin: 0, fontSize: '0.85rem', color: colour.text, lineHeight: '1.5', whiteSpace: 'pre-wrap', fontWeight: '500' }}>{note.content}</p>
                            </div>
                            <p style={{ margin: 0, fontSize: '0.68rem', color: '#AAA', paddingLeft: '0.25rem' }}>
                              {author} · {new Date(note.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        );
                      });
                    })()}
                  </div>
                  {/* Input */}
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', borderTop: '0.5px solid #E8E4E0', paddingTop: '0.75rem' }}>
                    <textarea
                      value={newNote}
                      onChange={e => setNewNote(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddNote(); } }}
                      placeholder="Add a note... (Enter to send, Shift+Enter for new line)"
                      rows={2}
                      style={{ flex: 1, border: '1px solid #D4CFC9', borderRadius: '10px', padding: '0.6rem 0.85rem', fontSize: '0.82rem', color: '#1A1A1A', fontFamily: 'inherit', resize: 'none', outline: 'none', lineHeight: '1.4' }}
                    />
                    <button onClick={handleAddNote} disabled={savingNote || !newNote.trim()}
                      style={{ fontSize: '0.82rem', color: '#fff', background: '#8B1A1A', border: 'none', borderRadius: '10px', padding: '0.6rem 0.9rem', cursor: 'pointer', opacity: !newNote.trim() ? 0.5 : 1, flexShrink: 0 }}>
                      {savingNote ? '...' : 'Send'}
                    </button>
                  </div>
                </div>
              )}
              {activeTab === 'seq-settings' && (
                <SequenceConfigPanel level="company" id={company.id} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
